import { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useWorkspaceDetails } from "@project-mgmt/feature-workspaces";
import { useProject } from "@project-mgmt/feature-projects";
import { useTask } from "@project-mgmt/feature-tasks";
import { useVenture } from "@project-mgmt/feature-ventures";
import type { BreadcrumbLevel } from "@project-mgmt/ui";
import { routes } from "../utils/routes";

interface RouteParamSet {
  workspaceId?: string;
  projectId?: string;
  taskId?: string;
  ventureId?: string;
}

export interface UseBreadcrumbHierarchyResult {
  levels: BreadcrumbLevel[];
  isLoading: boolean;
}

export function useBreadcrumbHierarchy(): UseBreadcrumbHierarchyResult {
  const location = useLocation();
  const { workspaceId, projectId, taskId, ventureId } = useParams() as RouteParamSet;

  const {
    data: workspaceData,
    isLoading: isWorkspaceLoading,
    error: workspaceError,
  } = useWorkspaceDetails(workspaceId ?? "");
  const { data: projectData, isLoading: isProjectLoading, error: projectError } = useProject(
    projectId ?? "",
    workspaceId ?? ""
  );
  const { data: taskData, isLoading: isTaskLoading, error: taskError } = useTask(taskId ?? "", workspaceId ?? "");
  const { venture, isLoading: isVentureLoading, error: ventureError } = useVenture(ventureId ?? "", workspaceId ?? "");

  const levels = useMemo(() => {
    const result: BreadcrumbLevel[] = [];
    const pathname = location.pathname;

    if (workspaceId) {
      result.push({
        label: workspaceData?.workspace?.name || workspaceId,
        path: routes.workspace(workspaceId),
        isLoading: isWorkspaceLoading && !workspaceData?.workspace && !workspaceError,
      });
    }

    if (projectId && workspaceId) {
      result.push({
        label: projectData?.name || projectId,
        path: routes.project(workspaceId, projectId),
        isLoading: isProjectLoading && !projectData && !projectError,
      });
    }

    if (ventureId && workspaceId) {
      result.push({
        label: venture?.name || ventureId,
        path: routes.ventureDetail(workspaceId, ventureId),
        isLoading: isVentureLoading && !venture && !ventureError,
      });
    }

    if (taskId && projectId && workspaceId) {
      result.push({
        label: taskData?.title || taskId,
        path: routes.task(workspaceId, projectId, taskId),
        isLoading: isTaskLoading && !taskData && !taskError,
      });
    }

    if (workspaceId && pathname === routes.projects(workspaceId)) {
      result.push({
        label: "Projects",
        path: routes.projects(workspaceId),
      });
    }

    if (workspaceId && pathname === routes.workspaceTasks(workspaceId)) {
      result.push({
        label: "Tasks",
        path: routes.workspaceTasks(workspaceId),
      });
    }

    if (workspaceId && projectId && pathname === routes.projectTasks(workspaceId, projectId)) {
      result.push({
        label: "Tasks",
        path: routes.projectTasks(workspaceId, projectId),
      });
    }

    if (workspaceId && pathname === routes.workspaceFiles(workspaceId)) {
      result.push({
        label: "Files",
        path: routes.workspaceFiles(workspaceId),
      });
    }

    if (workspaceId && projectId && taskId && pathname === routes.taskFiles(workspaceId, projectId, taskId)) {
      result.push({
        label: "Files",
        path: routes.taskFiles(workspaceId, projectId, taskId),
      });
    }

    if (workspaceId && pathname === routes.ventures(workspaceId)) {
      result.push({
        label: "Ventures",
        path: routes.ventures(workspaceId),
      });
    }

    if (workspaceId && pathname === routes.createVenture(workspaceId)) {
      result.push({
        label: "Ventures",
        path: routes.ventures(workspaceId),
      });
      result.push({
        label: "Create Venture",
        path: routes.createVenture(workspaceId),
      });
    }

    if (workspaceId && ventureId && pathname === routes.editVenture(workspaceId, ventureId)) {
      result.push({
        label: "Edit",
        path: routes.editVenture(workspaceId, ventureId),
      });
    }

    if (workspaceId && pathname === routes.notifications(workspaceId)) {
      result.push({
        label: "Notifications",
        path: routes.notifications(workspaceId),
      });
    }

    if (pathname === routes.myPledges()) {
      result.push({
        label: "My Pledges",
        path: routes.myPledges(),
      });
    }

    if (result.length > 0) {
      result[result.length - 1] = {
        ...result[result.length - 1],
        isCurrent: true,
      };
    }

    return result;
  }, [
    isProjectLoading,
    isTaskLoading,
    isVentureLoading,
    isWorkspaceLoading,
    location.pathname,
    projectData,
    projectError,
    projectId,
    taskData,
    taskError,
    taskId,
    venture,
    ventureError,
    ventureId,
    workspaceData,
    workspaceError,
    workspaceId,
  ]);

  return {
    levels,
    isLoading: isWorkspaceLoading || isProjectLoading || isTaskLoading || isVentureLoading,
  };
}
